import { ArrowDown, ArrowUp, Check, type LucideIcon } from "lucide-react";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import type { TOrganizationProjectDimension } from "@/zapi-frontend/shared/types/projects";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import type { TPortfolioSortDir } from "../../lib/portfolioStorage";
import { sortFields, sortOrderOptions } from "../../lib/sortOptions";
import { useTranslation } from "react-i18next";

type TProps = {
  sortFieldKey: string;
  sortDir: TPortfolioSortDir;
  dimensions: TOrganizationProjectDimension[];
  onSortFieldChange: (key: string) => void;
  onSortDirChange: (dir: TPortfolioSortDir) => void;
};

export function SortBySection(props: TProps) {
  const { sortFieldKey, sortDir, dimensions, onSortFieldChange, onSortDirChange } = props;
  const { t } = useTranslation("projects");
  const fields = sortFields(t, dimensions).filter((f) => f.key !== "modifiedBy");
  const orderOptions = sortOrderOptions(t, sortFieldKey);
  const showOrder = sortFieldKey !== "none";

  return (
    <>
      <div className={cn("p-1", showOrder && "shadow-[inset_0_-1px_0_#f4f4f5]")}>
        <SectionHeader>{t("sort.sortByHeading")}</SectionHeader>
        {fields.map((field) => (
          <SortOptionRow
            key={field.key}
            label={field.label}
            selected={sortFieldKey === field.key}
            onClick={() => onSortFieldChange(field.key)}
          />
        ))}
      </div>
      {showOrder && (
        <div className="p-1">
          <SectionHeader>{t("sort.orderHeading")}</SectionHeader>
          {orderOptions.map((option, i) => (
            <SortOptionRow
              key={option.dir}
              label={option.label}
              selected={sortDir === option.dir}
              trailingIcon={i === 0 ? ArrowDown : ArrowUp}
              onClick={() => onSortDirChange(option.dir)}
            />
          ))}
        </div>
      )}
    </>
  );
}

function SectionHeader({ children }: { children: string }) {
  return (
    <div className="flex h-8 items-center rounded-lg p-2">
      <FigmaTypography variant="label-2" color="zinc500">
        {children}
      </FigmaTypography>
    </div>
  );
}

type TRowProps = {
  label: string;
  selected: boolean;
  trailingIcon?: LucideIcon;
  onClick: () => void;
};

function SortOptionRow({ label, selected, trailingIcon: TrailingIcon, onClick }: TRowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className={cn(
        "flex w-full items-center rounded-md px-2 py-1.5 text-left hover:bg-innoveraZinc-50",
        selected && "bg-innoveraZinc-50"
      )}
    >
      <span className="flex min-w-0 items-center gap-2">
        <Check
          className={cn("size-4 shrink-0 text-innoveraZinc-900", !selected && "opacity-0")}
          strokeWidth={2}
          aria-hidden={!selected}
        />
        <FigmaTypography variant="body-2" color="zinc900" className="truncate">
          {label}
        </FigmaTypography>
        {TrailingIcon ? (
          <TrailingIcon className="size-4 shrink-0 text-innoveraZinc-900" strokeWidth={1.5} />
        ) : null}
      </span>
    </button>
  );
}
